import {
  MolsonBox,
  MolsonMenuItem,
  MolsonSelect,
  MolsonTypography
} from '@components/reusable'
import { FormControl } from '@mui/material'
import { useChangeCulture } from '@shared-queries'
import { type ReactNode, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { i18n } from '@/i18n/config'
import { getCultureLanguage } from './utils'

type Culture = 'en-GB' | 'en-US'

interface CultureSwitcherProps {
  currentCulture: Culture
  onCultureChange: (culture: Culture) => void
}

interface LanguageOption {
  label: string
  value: Culture
  flag: string
}

const languageOptions: LanguageOption[] = [
  { label: 'UK', value: 'en-GB', flag: '🇬🇧' },
  { label: 'US', value: 'en-US', flag: '🇺🇸' }
]

export const CultureSwitcher = ({
  currentCulture,
  onCultureChange
}: CultureSwitcherProps) => {
  const router = useNavigate()

  const { mutateAsync: handleSetCulture } = useChangeCulture()

  const handleLanguageChange = useCallback(
    async (event: { target: { value: unknown } }, _child: ReactNode) => {
      const newValue = event.target.value as Culture
      onCultureChange(newValue)

      // Change i18n language to match culture
      const i18nLanguage = getCultureLanguage(newValue)
      await i18n.changeLanguage(i18nLanguage)

      await handleSetCulture({ culture: newValue })

      router(0)
    },
    [handleSetCulture, router, onCultureChange]
  )

  return (
    <FormControl
      size="small"
      sx={{ minWidth: 100 }}
      data-testid="culture-switcher"
    >
      <MolsonSelect
        value={currentCulture}
        onChange={handleLanguageChange}
        renderValue={value => {
          const selected = languageOptions.find(
            option => option.value === value
          )
          return (
            <MolsonBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{selected?.flag}</span>
              <MolsonTypography variant="body2">
                {selected?.label}
              </MolsonTypography>
            </MolsonBox>
          )
        }}
        sx={{
          '.MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            padding: '6px 14px'
          }
        }}
      >
        {languageOptions.map(option => (
          <MolsonMenuItem key={option.value} value={option.value}>
            <MolsonBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{option.flag}</span>
              <MolsonTypography variant="body2">
                {option.label}
              </MolsonTypography>
            </MolsonBox>
          </MolsonMenuItem>
        ))}
      </MolsonSelect>
    </FormControl>
  )
}
